import { Icon } from "@iconify/react";
import { motion, AnimatePresence, useInView } from "framer-motion";
import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

// Hide scrollbar CSS
const hideScrollbarStyle = {
  scrollbarWidth: "none" as const,
  msOverflowStyle: "none" as const,
};

const Color = {
  aiPurple: "#9B59B6",
  themeBlue: "#4ea4f5",
  textPrimary: "#000",
  textSecondary: "#666",
  textHintStrong: "#979797",
  textThemeDark: "#1890ff",
  borderLight: "#e0e0e0",
  white: "#fff",
  bgLight: "#f8f9fa",
  userBubble: "#f5f5f5",
  aiBubble: "#fafafa",
  citationGray: "#f5f5f5",
  highlightYellow: "#fff3cd",
  // AI Model brand colors
  openai: "#10a37f",
  claude: "#d97706",
  gemini: "#4285f4",
  grok: "#000000",
};

interface Citation {
  id: number;
  label: string;
  type: "pdf" | "recording" | "youtube";
  title: string;
  content: string;
  highlight?: string;
  timestamp?: string;
  page?: number;
}

// Animation phases
type AnimationPhase =
  | "idle"           // Just chat bar visible
  | "showSidebar"    // Empty sidebar slides in
  | "zipInSources"   // Sources zip in one by one
  | "typing"         // Typing in input
  | "sending"        // Message sent animation
  | "searching"      // "Searching through notes" flash
  | "streaming"      // AI response streaming
  | "citationClick"  // Click on citation
  | "sourcesOpen"    // Sources panel opens
  | "rotatingCitations"; // Cycling through citations

interface FeatureSectionAIMemoryProps {
  showDescription?: boolean;
}

const FeatureSectionAIMemory: React.FC<FeatureSectionAIMemoryProps> = ({ showDescription = true }) => {
  const { t } = useTranslation();

  // Animation state
  const [phase, setPhase] = useState<AnimationPhase>("idle");
  const [showSourcesPanel, setShowSourcesPanel] = useState(false);
  const [showUserMessage, setShowUserMessage] = useState(false);
  const [showAIMessage, setShowAIMessage] = useState(false);
  const [typedText, setTypedText] = useState("");
  const [streamedText, setStreamedText] = useState("");
  const [streamedCitations, setStreamedCitations] = useState<number[]>([]);
  const [showSearching, setShowSearching] = useState(false);

  // Citation state
  const [animatingCitation, setAnimatingCitation] = useState<number | null>(null);
  const [expandedCitation, setExpandedCitation] = useState<number | null>(null);
  const [previewingCitation, setPreviewingCitation] = useState<number | null>(null);
  const [visibleSources, setVisibleSources] = useState<number[]>([]); // Track which sources have zipped in

  // Model state
  const [selectedModel, setSelectedModel] = useState<"gpt" | "claude" | "gemini" | "grok">("gpt");
  const [highlightedModel, setHighlightedModel] = useState<"gpt" | "claude" | "gemini" | "grok" | null>(null);

  // Bullet points
  const [showBulletPoints, setShowBulletPoints] = useState(false);
  const bulletPointsShownRef = useRef<boolean>(false);

  const animationStartedRef = useRef<boolean>(false);
  const cardRef = useRef<HTMLDivElement>(null);
  // Only animate when 90% of card is visible (Google's standard)
  const isInView = useInView(cardRef, { amount: 0.9 });

  const citations: Citation[] = [
    {
      id: 1,
      label: "[1]",
      type: "youtube",
      title: "YouWo AI Tutorial",
      content: "...real-time transcription supports over 100 languages with instant translation...",
      highlight: "100+ language transcription",
      timestamp: "02:15",
    },
    {
      id: 2,
      label: "[2]",
      type: "pdf",
      title: "YouWo AI Features Guide",
      content: "...AI-powered study tools generate quizzes and flashcards from any content...",
      highlight: "AI generates quizzes and flashcards",
      page: 8,
    },
    {
      id: 3,
      label: "[3]",
      type: "recording",
      title: "YouWo AI Demo",
      content: "...ask questions while recording and get instant answers with citations...",
      highlight: "ask AI while recording",
      timestamp: "05:42",
    },
  ];

  // Full message text (we'll stream this)
  const userMessageText = t("modern.aiMemory.mockup.userMessage") as string;
  const aiMessageParts = [
    { text: t("modern.aiMemory.mockup.aiMessage") as string, citation: null },
    { text: " ", citation: 1 },
    { text: ". " + (t("modern.aiMemory.mockup.feature1") as string), citation: null },
    { text: " ", citation: 2 },
    { text: ", " + (t("modern.aiMemory.mockup.feature2") as string), citation: null },
    { text: " ", citation: 3 },
    { text: ".", citation: null },
  ];

  // Main animation sequence
  useEffect(() => {
    if (!isInView) {
      animationStartedRef.current = false;
      return;
    }
    if (animationStartedRef.current) return;
    animationStartedRef.current = true;

    let isCancelled = false;

    const wait = (ms: number) => new Promise<boolean>(resolve => {
      const timeout = setTimeout(() => {
        if (isCancelled) resolve(false);
        else resolve(true);
      }, ms);
      return () => clearTimeout(timeout);
    });

    const typeText = async (text: string, setter: (s: string) => void, speed: number = 50) => {
      for (let i = 0; i <= text.length; i++) {
        if (isCancelled) return false;
        setter(text.slice(0, i));
        if (!await wait(speed)) return false;
      }
      return true;
    };

    const runAnimation = async () => {
      while (!isCancelled) {
        // ========== RESET STATE ==========
        setPhase("idle");
        setShowSourcesPanel(false);
        setShowUserMessage(false);
        setShowAIMessage(false);
        setTypedText("");
        setStreamedText("");
        setStreamedCitations([]);
        setShowSearching(false);
        setAnimatingCitation(null);
        setExpandedCitation(null);
        setPreviewingCitation(null);
        setHighlightedModel(null);
        if (!bulletPointsShownRef.current) {
          setShowBulletPoints(false);
        }

        // Initial pause
        if (!await wait(1000)) return;

        // ========== PHASE 0: SHOW EMPTY SIDEBAR ==========
        setPhase("showSidebar");
        setShowSourcesPanel(true);
        setVisibleSources([]); // Start with empty sidebar
        if (!await wait(600)) return;

        // ========== PHASE 0.5: ZIP IN SOURCES ONE BY ONE ==========
        setPhase("zipInSources");
        // Zip in each source with a delay
        for (let i = 0; i < citations.length; i++) {
          if (isCancelled) return;
          setVisibleSources(prev => [...prev, citations[i].id]);
          if (!await wait(350)) return; // Stagger delay between sources
        }
        if (!await wait(500)) return; // Pause after all sources are in

        // ========== PHASE 1: TYPE MESSAGE ==========
        setPhase("typing");
        if (!await typeText(userMessageText, setTypedText, 28)) return;
        if (!await wait(300)) return;

        // ========== PHASE 2: SEND MESSAGE ==========
        setPhase("sending");
        setTypedText("");
        setShowUserMessage(true);
        if (!await wait(400)) return;

        // ========== PHASE 3: SEARCHING (shown as AI message) ==========
        setPhase("searching");
        setShowAIMessage(true);
        setShowSearching(true);
        if (!await wait(1200)) return;
        setShowSearching(false);

        // ========== PHASE 4: STREAM AI RESPONSE ==========
        setPhase("streaming");

        // Stream each part with citations appearing as whole components
        let fullText = "";
        for (const part of aiMessageParts) {
          if (isCancelled) return;

          if (part.citation) {
            // Add citation as whole component (no animation, just appear)
            setStreamedCitations(prev => [...prev, part.citation!]);
          } else {
            // Stream text character by character (fast)
            for (let i = 0; i < part.text.length; i++) {
              if (isCancelled) return;
              fullText += part.text[i];
              setStreamedText(fullText);
              if (!await wait(12)) return;
            }
          }
        }

        if (!await wait(800)) return;

        // ========== PHASE 5: CLICK FIRST CITATION ==========
        setPhase("citationClick");
        // Preview hint - highlight before clicking
        setPreviewingCitation(1);
        if (!await wait(400)) return;
        // Click animation
        setPreviewingCitation(null);
        setAnimatingCitation(1);
        if (!await wait(300)) return;

        // ========== PHASE 6: OPEN SOURCES PANEL (after clicking citation) ==========
        setPhase("sourcesOpen");
        setShowSourcesPanel(true);
        if (!await wait(400)) return; // Wait for panel to slide in
        setAnimatingCitation(null);
        if (!await wait(300)) return; // Delay before source changes
        setExpandedCitation(1);
        if (!await wait(500)) return;

        // ========== PHASE 7: ROTATE THROUGH CITATIONS (skip first, already expanded) ==========
        setPhase("rotatingCitations");

        for (let i = 1; i < citations.length; i++) {
          if (isCancelled) return;

          // Preview hint - highlight before clicking
          setPreviewingCitation(citations[i].id);
          if (!await wait(350)) return;
          // Click the citation
          setPreviewingCitation(null);
          setAnimatingCitation(citations[i].id);
          if (!await wait(300)) return; // Delay before source changes
          setExpandedCitation(citations[i].id);
          if (!await wait(250)) return;
          setAnimatingCitation(null);
          if (!await wait(400)) return;
        }

        // Extra pause after citations
        if (!await wait(750)) return;

        // Show bullet points
        setShowBulletPoints(true);
        bulletPointsShownRef.current = true;

        // Wait before restarting (8 seconds)
        if (!await wait(8000)) return;
      }
    };

    runAnimation();

    return () => {
      isCancelled = true;
    };
  }, [isInView, userMessageText]);

  // Handle citation click
  const handleCitationClick = (citationId: number) => {
    setAnimatingCitation(citationId);
    setExpandedCitation(citationId);
    if (!showSourcesPanel) setShowSourcesPanel(true);
    setTimeout(() => setAnimatingCitation(null), 500);
  };

  const renderCitationBadge = (citationId: number, isInStream: boolean = false) => {
    const citation = citations.find(c => c.id === citationId);
    if (!citation) return null;

    // Don't render if not yet streamed (during streaming phase)
    if (isInStream && !streamedCitations.includes(citationId)) return null;

    const isActive = animatingCitation === citationId;
    const isPreviewing = previewingCitation === citationId;
    const citationInfo = citation.timestamp ? citation.timestamp : `P${citation.page}`;

    return (
      <motion.button
        key={`citation-${citationId}`}
        onClick={() => handleCitationClick(citationId)}
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full mx-0.5 cursor-pointer hover:bg-blue-100 transition-all"
        style={{
          backgroundColor: `${Color.themeBlue}15`,
          color: Color.themeBlue,
        }}
        animate={
          isPreviewing
            ? { scale: 1.1, opacity: 1, boxShadow: "0 0 12px rgba(78, 164, 245, 0.5)" }
            : isActive
              ? { scale: [1.1, 1.2, 1], opacity: 1, boxShadow: ["0 0 12px rgba(78, 164, 245, 0.5)", "0 0 15px rgba(78, 164, 245, 0.6)", "0 0 0 rgba(78, 164, 245, 0)"] }
              : { scale: 1, opacity: 1, boxShadow: "0 0 0 rgba(78, 164, 245, 0)" }
        }
        transition={{ duration: isPreviewing ? 0.25 : 0.2, ease: "easeOut" }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        <span className="text-[11px]">{citation.label}</span>
        <span className="text-[11px]">{citationInfo}</span>
      </motion.button>
    );
  };

  // Render streamed AI message with citations
  const renderStreamedMessage = () => {
    const parts: React.ReactNode[] = [];
    let textIndex = 0;

    for (let i = 0; i < aiMessageParts.length; i++) {
      const part = aiMessageParts[i];

      if (part.citation) {
        // Render citation badge if it's been streamed
        if (streamedCitations.includes(part.citation)) {
          parts.push(renderCitationBadge(part.citation, true));
        }
      } else {
        // Render text up to what's been streamed
        const partLength = part.text.length;
        const availableChars = streamedText.length - textIndex;
        const charsToShow = Math.min(partLength, Math.max(0, availableChars));

        if (charsToShow > 0) {
          parts.push(<span key={`text-${i}`}>{part.text.slice(0, charsToShow)}</span>);
        }
        textIndex += partLength;
      }
    }

    return parts;
  };

  return (
    <section className={`${showDescription ? 'py-8 md:py-20' : 'py-0'} bg-white overflow-hidden`}>
      <div className={showDescription ? "container mx-auto px-4" : ""}>
        <div className={`${showDescription ? 'grid md:grid-cols-2 gap-12 lg:gap-20 items-center max-w-7xl mx-auto' : 'flex justify-center'}`}>
          {/* Left - Text Content */}
          {showDescription && <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6"
              style={{ backgroundColor: `${Color.aiPurple}15` }}
            >
              <Icon icon="lucide:sparkles" style={{ color: Color.aiPurple }} />
              <span style={{ color: Color.aiPurple }} className="font-medium text-sm">
                {t("modern.aiMemory.badge")}
              </span>
            </div>

            <h2 className="text-11xl md:text-17xl lg:text-29xl font-bold text-gray-900 mb-6 leading-tight mt-0">
              {t("modern.aiMemory.title")}
            </h2>

            <p className="text-lg md:text-xl mb-8 leading-relaxed" style={{ color: "#726F6F" }}>
              {t("modern.aiMemory.subtitle")}
            </p>

            {/* Mobile version - always visible */}
            <div className="block md:hidden space-y-4">
              {[
                { icon: "lucide:message-circle", text: t("modern.aiMemory.bullets.chat") },
                { icon: "lucide:file-search", text: t("modern.aiMemory.bullets.jumpToSource") },
                { icon: "lucide:globe", text: t("modern.aiMemory.bullets.webSearch") },
              ].map((item, index) => (
                <div key={index} className="flex items-center gap-3">
                  <div
                    className="w-9 h-9 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: `${Color.aiPurple}15` }}
                  >
                    <Icon icon={item.icon} className="text-lg" style={{ color: Color.aiPurple }} />
                  </div>
                  <span className="text-base text-gray-700">{item.text}</span>
                </div>
              ))}
            </div>

            {/* Desktop version - animated */}
            <div className="hidden md:block" style={{ minHeight: 140 }}>
              <motion.div
                className="space-y-4"
                initial={{ opacity: 0 }}
                animate={{ opacity: showBulletPoints ? 1 : 0 }}
                transition={{ duration: 0.4 }}
              >
                {[
                  { icon: "lucide:message-circle", text: t("modern.aiMemory.bullets.chat") },
                  { icon: "lucide:file-search", text: t("modern.aiMemory.bullets.jumpToSource") },
                  { icon: "lucide:globe", text: t("modern.aiMemory.bullets.webSearch") },
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    className="flex items-center gap-3"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: showBulletPoints ? 1 : 0, x: showBulletPoints ? 0 : -20 }}
                    transition={{ delay: showBulletPoints ? index * 0.15 : 0, duration: 0.4 }}
                  >
                    <div
                      className="w-9 h-9 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${Color.aiPurple}15` }}
                    >
                      <Icon icon={item.icon} className="text-lg" style={{ color: Color.aiPurple }} />
                    </div>
                    <span className="text-base text-gray-700">{item.text}</span>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </motion.div>}

          {/* Right - Visual Mockup */}
          <motion.div
            initial={{ opacity: 0, x: showDescription ? 50 : 0 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: showDescription ? 0.2 : 0 }}
            className="px-4 md:px-0"
          >
            <div className="relative w-full max-w-[580px] mx-auto">
                {/* Floating badge */}
                <motion.div
                  className="px-4 py-2 rounded-full shadow-lg flex items-center gap-2 absolute z-10"
                  style={{
                    top: -6,
                    right: -6,
                    backgroundColor: Color.aiPurple,
                  }}
                  animate={{ y: [0, -5, 0] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <Icon icon="lucide:quote" className="text-white" />
                  <span className="text-white text-sm font-medium">{t("modern.aiMemory.mockup.sourceBacked")}</span>
                </motion.div>

                {/* Chat Interface Mockup */}
                <div
                  ref={cardRef}
                  className="rounded-3xl overflow-hidden shadow-2xl"
                  style={{
                    backgroundColor: Color.white,
                    border: `1px solid ${Color.borderLight}`,
                  }}
                >
                  {/* Header */}
                  <div
                    className="px-4 py-3 flex items-center gap-3"
                    style={{ borderBottom: `1px solid ${Color.borderLight}` }}
                  >
                    <Icon icon="lucide:folder" className="text-gray-400" />
                    <span className="font-semibold text-gray-700">{t("modern.aiMemory.mockup.header")}</span>
                    <span className="text-xs px-2 py-1 rounded-full ml-auto text-gray-500" style={{ backgroundColor: "#f5f5f5" }}>
                      {t("modern.aiMemory.mockup.sources")}
                    </span>
                  </div>

                  {/* Main Content */}
                  <div className="flex" style={{ minHeight: 440 }}>
                    {/* Left: Sources Panel - animated slide in */}
                    <AnimatePresence>
                      {showSourcesPanel && (
                        <motion.div
                          initial={{ width: 0, opacity: 0 }}
                          animate={{ width: 208, opacity: 1 }}
                          exit={{ width: 0, opacity: 0 }}
                          transition={{ duration: 0.4, ease: "easeOut" }}
                          className="flex-shrink-0 overflow-hidden"
                          style={{
                            backgroundColor: Color.bgLight,
                            borderRight: `1px solid ${Color.borderLight}`,
                            minHeight: 380,
                          }}
                        >
                          <div className="w-52 p-3 overflow-y-auto h-full" style={{ ...hideScrollbarStyle, minHeight: 360 }}>
                            <div className="flex items-center gap-2 mb-3">
                              <Icon icon="lucide:file-search" className="text-sm text-gray-400" />
                              <span className="text-xs font-semibold text-gray-500">{t("modern.aiMemory.mockup.jumpToSource")}</span>
                            </div>

                            {/* Citation Cards */}
                            <div className="space-y-2">
                              <AnimatePresence>
                              {citations.filter(c => visibleSources.includes(c.id)).map((citation, index) => {
                                const isExpanded = expandedCitation === citation.id;
                                const isAnimating = animatingCitation === citation.id;
                                // Get icon color based on type
                                const getIconColor = () => {
                                  if (isExpanded) return Color.themeBlue;
                                  switch (citation.type) {
                                    case "youtube": return "#ff0000"; // red for YouTube
                                    case "recording": return "#3b82f6"; // blue for recording
                                    case "pdf": return "#f97316"; // orange for PDF
                                    default: return "#9ca3af";
                                  }
                                };

                                // Get icon based on type
                                const getIcon = () => {
                                  switch (citation.type) {
                                    case "youtube": return "logos:youtube-icon";
                                    case "recording": return "lucide:mic";
                                    case "pdf": return "lucide:file-text";
                                    default: return "lucide:file";
                                  }
                                };

                                return (
                                  <motion.div
                                    key={citation.id}
                                    className="rounded-lg cursor-pointer overflow-hidden"
                                    style={{
                                      backgroundColor: isExpanded ? Color.white : "rgba(255,255,255,0.5)",
                                      border: isExpanded ? `1px solid ${Color.themeBlue}` : "1px solid transparent",
                                      boxShadow: isExpanded ? "0 2px 8px rgba(78, 164, 245, 0.15)" : "none",
                                    }}
                                    initial={{ opacity: 0, x: -30, scale: 0.9 }}
                                    animate={{
                                      opacity: 1,
                                      x: 0,
                                      scale: isAnimating ? [1, 1.02, 1] : 1
                                    }}
                                    exit={{ opacity: 0, x: -20 }}
                                    transition={{
                                      duration: 0.35,
                                      ease: "easeOut",
                                      delay: index * 0.05 // Small stagger for visual effect
                                    }}
                                    onClick={() => handleCitationClick(citation.id)}
                                  >
                                    <div className="p-2 flex items-center gap-2">
                                      <Icon
                                        icon={getIcon()}
                                        className="text-sm flex-shrink-0"
                                        style={citation.type === "youtube" ? {} : { color: getIconColor() }}
                                      />
                                      <span className={`text-xs ${isExpanded ? "font-medium text-gray-800" : "text-gray-600"}`}>
                                        {citation.title}
                                      </span>
                                      <motion.div
                                        className="ml-auto"
                                        animate={{ rotate: isExpanded ? 180 : 0 }}
                                        transition={{ duration: 0.2 }}
                                      >
                                        <Icon icon="lucide:chevron-down" className="text-xs text-gray-400" />
                                      </motion.div>
                                    </div>

                                    <motion.div
                                      initial={false}
                                      animate={{
                                        height: isExpanded ? "auto" : 0,
                                        opacity: isExpanded ? 1 : 0,
                                      }}
                                      transition={{ duration: 0.2 }}
                                      style={{ overflow: "hidden" }}
                                    >
                                      <div className="px-2 pb-2">
                                        <p className="text-xs text-gray-500 mb-1.5">
                                          {citation.timestamp ? citation.timestamp : `Page ${citation.page}`}
                                        </p>
                                        <p className="text-xs text-gray-600 leading-relaxed">
                                          "...
                                          <span className="px-0.5 rounded" style={{ backgroundColor: Color.highlightYellow }}>
                                            {citation.highlight}
                                          </span>
                                          ..."
                                        </p>
                                      </div>
                                    </motion.div>
                                  </motion.div>
                                );
                              })}
                              </AnimatePresence>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    {/* Right: Chat Area */}
                    <div className="flex-1 flex flex-col">
                      <div className="flex-1 p-4 space-y-4">
                        {/* User Message */}
                        <AnimatePresence>
                          {showUserMessage && (
                            <motion.div
                              initial={{ opacity: 0, y: 10, scale: 0.95 }}
                              animate={{ opacity: 1, y: 0, scale: 1 }}
                              className="flex justify-end"
                            >
                              <div
                                className="max-w-[85%] px-2.5 py-1 rounded-2xl"
                                style={{ backgroundColor: `${Color.themeBlue}20`, color: Color.textPrimary }}
                              >
                                <p className="text-sm">{userMessageText}</p>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>

                        {/* AI Message - Streamed */}
                        <AnimatePresence>
                          {showAIMessage && (
                            <motion.div
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              className="flex justify-start"
                            >
                              <div
                                className="max-w-[85%] px-2.5 py-1.5 rounded-2xl"
                                style={{ backgroundColor: Color.white, border: `1px solid ${Color.borderLight}` }}
                              >
                                {showSearching ? (
                                  <div className="flex items-center gap-2 py-0.5">
                                    <motion.div
                                      animate={{ rotate: 360 }}
                                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                                    >
                                      <Icon icon="lucide:loader-2" className="text-gray-400 text-sm" />
                                    </motion.div>
                                    <span className="text-sm text-gray-500">Searching through your notes...</span>
                                  </div>
                                ) : (
                                  <p className="text-sm text-gray-700 leading-relaxed">
                                    {renderStreamedMessage()}
                                    {phase === "streaming" && (
                                      <motion.span
                                        animate={{ opacity: [1, 0] }}
                                        transition={{ duration: 0.5, repeat: Infinity }}
                                        className="inline-block w-0.5 h-4 bg-gray-400 ml-0.5 align-middle"
                                      />
                                    )}
                                  </p>
                                )}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>

                      {/* Chat Input */}
                      <div
                        className="p-3"
                        style={{ borderTop: `1px solid ${Color.borderLight}` }}
                      >
                        {/* Model Selector */}
                        <div className="flex items-center gap-1.5 mb-2">
                          {/* GPT */}
                          <motion.div
                            className="flex items-center gap-1 px-2 py-1 rounded-lg cursor-pointer transition-colors"
                            style={selectedModel === "gpt"
                              ? { backgroundColor: highlightedModel === "gpt" ? `${Color.openai}20` : `${Color.themeBlue}15`, border: `1px solid ${highlightedModel === "gpt" ? Color.openai : Color.themeBlue}` }
                              : { backgroundColor: "#f9fafb", border: "1px solid transparent" }
                            }
                            animate={highlightedModel === "gpt" ? { scale: [1, 1.1, 1.05] } : { scale: 1 }}
                            transition={{ duration: 0.3 }}
                            onClick={() => setSelectedModel("gpt")}
                          >
                            <Icon icon="simple-icons:openai" className="text-xs" style={{ color: highlightedModel === "gpt" ? Color.openai : selectedModel === "gpt" ? Color.themeBlue : "#9ca3af" }} />
                            <span className="text-xs font-medium" style={{ color: highlightedModel === "gpt" ? Color.openai : selectedModel === "gpt" ? Color.themeBlue : "#9ca3af" }}>GPT</span>
                          </motion.div>
                          {/* Claude */}
                          <motion.div
                            className="flex items-center gap-1 px-2 py-1 rounded-lg cursor-pointer transition-colors"
                            style={selectedModel === "claude"
                              ? { backgroundColor: highlightedModel === "claude" ? `${Color.claude}20` : `${Color.themeBlue}15`, border: `1px solid ${highlightedModel === "claude" ? Color.claude : Color.themeBlue}` }
                              : { backgroundColor: "#f9fafb", border: "1px solid transparent" }
                            }
                            animate={highlightedModel === "claude" ? { scale: [1, 1.1, 1.05] } : { scale: 1 }}
                            transition={{ duration: 0.3 }}
                            onClick={() => setSelectedModel("claude")}
                          >
                            <Icon icon="simple-icons:claude" className="text-xs" style={{ color: highlightedModel === "claude" ? Color.claude : selectedModel === "claude" ? Color.themeBlue : "#9ca3af" }} />
                            <span className="text-xs font-medium" style={{ color: highlightedModel === "claude" ? Color.claude : selectedModel === "claude" ? Color.themeBlue : "#9ca3af" }}>Claude</span>
                          </motion.div>
                          {/* Gemini */}
                          <motion.div
                            className="flex items-center gap-1 px-2 py-1 rounded-lg cursor-pointer transition-colors"
                            style={selectedModel === "gemini"
                              ? { backgroundColor: highlightedModel === "gemini" ? `${Color.gemini}20` : `${Color.themeBlue}15`, border: `1px solid ${highlightedModel === "gemini" ? Color.gemini : Color.themeBlue}` }
                              : { backgroundColor: "#f9fafb", border: "1px solid transparent" }
                            }
                            animate={highlightedModel === "gemini" ? { scale: [1, 1.1, 1.05] } : { scale: 1 }}
                            transition={{ duration: 0.3 }}
                            onClick={() => setSelectedModel("gemini")}
                          >
                            <Icon icon="simple-icons:googlegemini" className="text-xs" style={{ color: highlightedModel === "gemini" ? Color.gemini : selectedModel === "gemini" ? Color.themeBlue : "#9ca3af" }} />
                            <span className="text-xs font-medium" style={{ color: highlightedModel === "gemini" ? Color.gemini : selectedModel === "gemini" ? Color.themeBlue : "#9ca3af" }}>Gemini</span>
                          </motion.div>
                          {/* Grok */}
                          <motion.div
                            className="flex items-center gap-1 px-2 py-1 rounded-lg cursor-pointer transition-colors"
                            style={selectedModel === "grok"
                              ? { backgroundColor: highlightedModel === "grok" ? `${Color.grok}15` : `${Color.themeBlue}15`, border: `1px solid ${highlightedModel === "grok" ? Color.grok : Color.themeBlue}` }
                              : { backgroundColor: "#f9fafb", border: "1px solid transparent" }
                            }
                            animate={highlightedModel === "grok" ? { scale: [1, 1.1, 1.05] } : { scale: 1 }}
                            transition={{ duration: 0.3 }}
                            onClick={() => setSelectedModel("grok")}
                          >
                            <img src="/logo/grok.svg" alt="Grok" className="w-3 h-3" style={{ opacity: highlightedModel === "grok" ? 1 : selectedModel === "grok" ? 1 : 0.4 }} />
                            <span className="text-xs font-medium" style={{ color: highlightedModel === "grok" ? Color.grok : selectedModel === "grok" ? Color.themeBlue : "#9ca3af" }}>Grok</span>
                          </motion.div>
                        </div>

                        {/* Input Field */}
                        <div
                          className="flex items-center gap-2 px-3 py-2 rounded-xl"
                          style={{ backgroundColor: Color.white, border: `1px solid ${Color.borderLight}` }}
                        >
                          <Icon icon="lucide:plus" className="text-sm text-gray-300 flex-shrink-0" />
                          <div className="flex-1 text-sm text-gray-700 min-h-[20px]">
                            {phase === "typing" ? (
                              <>
                                {typedText}
                                <motion.span
                                  animate={{ opacity: [1, 0] }}
                                  transition={{ duration: 0.5, repeat: Infinity }}
                                  className="inline-block w-0.5 h-4 bg-gray-400 ml-0.5 align-middle"
                                />
                              </>
                            ) : (
                              <span className="text-gray-300">{t("modern.aiMemory.mockup.messagePlaceholder")}</span>
                            )}
                          </div>
                          <motion.button
                            className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
                            style={{ backgroundColor: phase === "typing" && typedText.length > 0 ? Color.themeBlue : "#e5e7eb" }}
                            animate={phase === "sending" ? { scale: [1, 0.9, 1] } : {}}
                          >
                            <Icon icon="lucide:arrow-up" className={phase === "typing" && typedText.length > 0 ? "text-white text-sm" : "text-gray-400 text-sm"} />
                          </motion.button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default FeatureSectionAIMemory;
